import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, XCircle } from "lucide-react";

export function CheckoutSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState({ state: "polling", msg: "Checking payment status…" });
  const polled = useRef(0);

  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    const sid = sp.get("session_id");
    if (!sid) { setStatus({ state: "error", msg: "Missing session id." }); return; }
    let cancelled = false;
    const poll = async () => {
      polled.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setStatus({ state: "ok", msg: "Payment received. Your access is unlocked." });
          return;
        }
        if (data.status === "expired") { setStatus({ state: "error", msg: "Checkout session expired." }); return; }
        if (polled.current >= 8) { setStatus({ state: "warn", msg: "Still processing — check your email for confirmation." }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (polled.current >= 8) { setStatus({ state: "error", msg: "Could not verify payment." }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [loc.search, refresh]);

  return (
    <div className="bg-cream min-h-screen flex items-center justify-center p-6">
      <div className="editorial-card p-10 max-w-lg text-center">
        {status.state === "polling" && <Loader2 className="w-10 h-10 text-plum animate-spin mx-auto" />}
        {status.state === "ok" && <Check className="w-12 h-12 text-plum mx-auto" />}
        {status.state === "error" && <XCircle className="w-12 h-12 text-burgundy mx-auto" />}
        <h1 className="font-serif text-3xl mt-6">{status.state === "ok" ? "You're in." : status.state === "error" ? "Something went wrong." : "One moment…"}</h1>
        <p className="mt-4 text-charcoal/70">{status.msg}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => nav("/member")} className="btn-primary" data-testid="checkout-success-member">Go to my chambers</button>
          <Link to="/" className="btn-outline" data-testid="checkout-success-home">Home</Link>
        </div>
      </div>
    </div>
  );
}

export function CheckoutCancel() {
  return (
    <div className="bg-cream min-h-screen flex items-center justify-center p-6">
      <div className="editorial-card p-10 max-w-lg text-center">
        <h1 className="font-serif text-3xl">Checkout cancelled.</h1>
        <p className="mt-4 text-charcoal/70">No worries — nothing was charged. Auntie's still here whenever you're ready.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/membership" className="btn-primary" data-testid="checkout-cancel-back">Back to membership</Link>
          <Link to="/" className="btn-outline" data-testid="checkout-cancel-home">Home</Link>
        </div>
      </div>
    </div>
  );
}
